import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Camera, Check, RotateCcw } from 'lucide-react';
import { CAMERA_FORMATS, CAMERA_MESSAGES, normalizeCameraCode, webCameraAvailable } from '../inventoryTransfers/cameraScan';
import CameraScanSheet from '../inventoryTransfers/CameraScanSheet';

const RETURN_CAMERA_FORMATS = [...CAMERA_FORMATS, 'DataMatrix'];
const localDate = (value, withTime = false) => {
  if (!value) return 'N/A';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(`${value}T12:00:00Z`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: 'numeric', minute: '2-digit' } : {}),
  }).format(date) + (withTime ? ' PT' : '');
};

const ReturnsReceiving = ({ canEdit = false }) => {
  const [code, setCode] = useState('');
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraMessage, setCameraMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const inputRef = useRef(null);
  const cameraAvailable = webCameraAvailable();

  const loadRecent = useCallback(async (cancelToken) => {
    try {
      const { data } = await axios.get('/api/v1/returns/recent', cancelToken ? { cancelToken } : {});
      setRecent(data);
    } catch (error) {
      if (!axios.isCancel(error)) setRecent([]);
    }
  }, []);
  useEffect(() => {
    const source = axios.CancelToken.source();
    loadRecent(source.token);
    return () => source.cancel();
  }, [loadRecent]);

  const lookup = async (nextCode = code) => {
    const value = String(nextCode || '').trim();
    if (!value || busy || !canEdit) return;
    setBusy(true);
    setCode('');
    setCameraMessage('');
    setActionError('');
    setResult(null);
    try {
      const { data } = await axios.post('/api/v1/returns/lookup', { code: value });
      setResult({ ...data, kind: data.result });
    } catch (error) {
      if (!axios.isCancel(error)) {
        const response = error?.response?.data;
        setResult({
          ...response,
          kind: response?.code === 'not_tracking' && response.event_id ? 'invalid' : 'error',
          message: response?.error || 'Could not look up this scan. Try again.',
          raw: value,
        });
      }
    } finally {
      setBusy(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const decide = async (action) => {
    if (!result?.event_id || busy || !canEdit) return;
    setBusy(true);
    setActionError('');
    try {
      const endpoint = action === 'accept' ? '/api/v1/returns/accept' : '/api/v1/returns/reject';
      const { data } = await axios.post(endpoint, { scan_event_id: result.event_id });
      if (action === 'accept' && data.duplicate) {
        setResult({
          kind: 'duplicate',
          row: data.row,
          received_at: data.received_at,
          received_by: data.received_by,
        });
      } else if (action === 'accept') {
        const dueDate = data.row?.due_date;
        setResult({
          kind: 'confirmation',
          action: 'accepted',
          event_id: data.event_id,
          message: dueDate ? `Received · due ${localDate(dueDate)}` : 'Received',
        });
      } else {
        setResult({
          kind: 'confirmation',
          action: 'rejected',
          event_id: data.event_id,
          message: 'Rejected',
        });
      }
      await loadRecent();
    } catch (error) {
      if (!axios.isCancel(error)) {
        setActionError(error?.response?.data?.error || 'Could not save this decision. Try again.');
      }
    } finally {
      setBusy(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const undo = async (eventId) => {
    if (!eventId || busy || !canEdit) return;
    setBusy(true);
    setActionError('');
    try {
      await axios.post('/api/v1/returns/undo', { event_id: eventId });
      if (result?.kind === 'confirmation' && Number(result.event_id) === Number(eventId)) {
        setResult({ kind: 'confirmation', action: 'undone', message: 'Undone' });
      }
      await loadRecent();
    } catch (error) {
      if (!axios.isCancel(error)) {
        setActionError(error?.response?.data?.error || 'Could not undo this decision. Try again.');
      }
    } finally {
      setBusy(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const onRead = (read) => {
    setCameraOpen(false);
    const value = normalizeCameraCode(read);
    if (value) lookup(value);
  };
  const onCameraError = (errorCode) => {
    setCameraOpen(false);
    setCameraMessage(CAMERA_MESSAGES[errorCode] || CAMERA_MESSAGES.unavailable);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const row = result?.row || {};
  const theme = result?.kind === 'matched' ? 'border-green-300 bg-green-50 text-green-900'
    : result?.kind === 'unmatched' ? 'border-amber-300 bg-amber-50 text-amber-950'
      : result?.kind === 'duplicate' ? 'border-gray-300 bg-gray-50 text-gray-800'
        : result?.kind === 'invalid' ? 'border-red-300 bg-red-50 text-red-900' : '';

  return (
    <main className="mx-auto w-full max-w-xl space-y-4 p-3">
      <h1 className="text-xl font-semibold text-gray-900">Returns Receiving</h1>
      {!cameraAvailable && <p className="text-sm text-gray-500">{CAMERA_MESSAGES.unavailable}</p>}
      {cameraMessage && <p role="status" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{cameraMessage}</p>}
      {actionError && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">{actionError}</p>}
      <form
        onSubmit={(event) => { event.preventDefault(); lookup(); }}
        className="flex items-center gap-2"
        autoComplete="off"
      >
        <input
          ref={inputRef}
          value={code}
          onChange={(event) => setCode(event.target.value)}
          type="text"
          aria-label="Scan or type return tracking number"
          placeholder="Scan or type tracking number"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="go"
          disabled={!canEdit || busy}
          className="h-10 min-w-0 flex-1 rounded-lg border-2 border-gray-700 px-3 text-base"
        />
        <button
          type="button"
          aria-label="Scan with camera"
          title="Scan with camera"
          onClick={() => { setCameraMessage(''); setCameraOpen(true); }}
          disabled={!canEdit || !cameraAvailable || busy}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-400 text-gray-800 disabled:opacity-50"
        >
          <Camera size={19} />
        </button>
        <button type="submit" disabled={!canEdit || busy || !code.trim()} className="h-10 shrink-0 rounded-lg bg-gray-900 px-3 text-sm font-medium text-white disabled:opacity-50">
          {busy ? 'Working…' : 'Look up'}
        </button>
      </form>

      {result && ['matched', 'unmatched', 'duplicate', 'invalid', 'error'].includes(result.kind) && (
        <section role="status" className={`rounded-lg border p-3 ${theme || 'border-red-300 bg-red-50 text-red-900'}`}>
          <div className="flex items-center gap-2 font-semibold">
            {result.kind === 'matched' ? <><Check size={18} /> Matched return</>
              : result.kind === 'unmatched' ? <><AlertTriangle size={18} /> No RMA match — will be saved as an exception</>
                : result.kind === 'duplicate' ? <><RotateCcw size={18} /> Already received</>
                  : result.kind === 'invalid' ? 'Not a return tracking number' : 'Scan could not be saved'}
          </div>
          {result.kind === 'duplicate' ? (
            <p className="mt-1 text-sm">
              Already received {localDate(result.received_at, true)}{result.received_by ? ` by ${result.received_by}` : ''}.
            </p>
          ) : result.kind === 'invalid' || result.kind === 'error' ? (
            <p className="mt-1 text-sm">{result.message}</p>
          ) : (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              <dt className="font-medium">Tracking</dt><dd className="break-all">{row.tracking_number || result.tracking_number}</dd>
              {result.kind === 'matched' && (
                <>
                  <dt className="font-medium">RMA</dt><dd>{row.rma || 'RMA not assigned'}</dd>
                  <dt className="font-medium">Order #</dt><dd>{row.order_number || 'N/A'}</dd>
                  {row.is_warranty && <><dt className="font-medium">Flag</dt><dd>Warranty</dd></>}
                </>
              )}
              {result.warnings?.includes('rma_on_other_package') && (
                <><dt className="font-medium">Warning</dt><dd>This RMA was received on another tracking number.</dd></>
              )}
            </dl>
          )}
          <div className="mt-3 flex gap-2">
            {['matched', 'unmatched'].includes(result.kind) && (
              <>
                <button
                  type="button"
                  onClick={() => decide('accept')}
                  disabled={!canEdit || busy}
                  className="min-h-12 flex-1 rounded-lg bg-green-700 px-4 font-semibold text-white disabled:opacity-50"
                >
                  Accept
                </button>
                <button
                  type="button"
                  onClick={() => decide('reject')}
                  disabled={!canEdit || busy}
                  className="min-h-12 flex-1 rounded-lg border border-red-600 bg-white px-4 font-semibold text-red-700 disabled:opacity-50"
                >
                  Reject
                </button>
              </>
            )}
            {['duplicate', 'invalid'].includes(result.kind) && (
              <button
                type="button"
                onClick={() => decide('reject')}
                disabled={!canEdit || busy || !result.event_id}
                className="min-h-11 rounded-lg border border-gray-400 bg-white px-4 text-sm font-medium disabled:opacity-50"
              >
                Dismiss
              </button>
            )}
          </div>
        </section>
      )}

      {result?.kind === 'confirmation' && (
        <section role="status" className={`flex min-h-12 items-center justify-between gap-3 rounded-lg border p-3 text-sm ${
          result.action === 'accepted' ? 'border-green-300 bg-green-50 text-green-900' : 'border-gray-300 bg-gray-50 text-gray-800'
        }`}>
          <span className="font-medium">{result.message}</span>
          {result.action !== 'undone' && (
            <button
              type="button"
              onClick={() => undo(result.event_id)}
              disabled={!canEdit || busy}
              className="min-h-10 rounded px-2 font-semibold underline disabled:opacity-50"
            >
              Undo
            </button>
          )}
        </section>
      )}

      <section>
        <h2 className="mb-2 text-lg font-semibold text-gray-900">Today’s returns</h2>
        {recent.length ? (
          <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
            {recent.map((event) => {
              const processed = event.action === 'accepted' && !event.undone && event.processed;
              const status = event.undone ? 'Undone' : event.action === 'accepted' ? 'Received' : 'Rejected';
              return (
                <li key={event.id} className="flex min-h-14 items-center gap-3 px-3 py-2">
                  <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                    event.undone ? 'bg-gray-400' : event.action === 'accepted' ? 'bg-green-600' : 'bg-red-600'
                  }`} />
                  <span className="min-w-0 flex-1">
                    <span className={`block truncate font-medium text-gray-900 ${event.undone ? 'line-through' : ''}`}>{event.tracking_number || 'Invalid scan'}</span>
                    <span className="block truncate text-xs text-gray-600">
                      {event.rma || (event.result === 'unmatched' ? 'Unmatched' : 'No RMA')}
                      {event.order_number ? ` · ${event.order_number}` : ''}
                    </span>
                    <span className={`block text-xs font-medium ${event.undone ? 'line-through text-gray-500' : 'text-gray-700'}`}>
                      {processed ? 'Received · ' : status}
                      {processed && (
                        <span className="ml-1 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800">
                          Processed
                        </span>
                      )}
                      {event.user ? ` · ${event.user}` : ''}
                    </span>
                  </span>
                  <span className="text-right text-xs text-gray-600">{localDate(event.created_at, true)}</span>
                  {event.can_undo && (
                    <button
                      type="button"
                      onClick={() => undo(event.id)}
                      disabled={!canEdit || busy}
                      className="min-h-10 shrink-0 px-1 text-xs font-semibold text-blue-700 underline disabled:opacity-50"
                    >
                      Undo
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        ) : <p className="rounded-lg border border-dashed p-4 text-sm text-gray-500">No return decisions today.</p>}
      </section>
      {cameraOpen && (
        <CameraScanSheet
          formats={RETURN_CAMERA_FORMATS}
          onRead={onRead}
          onError={onCameraError}
          onClose={() => setCameraOpen(false)}
        />
      )}
    </main>
  );
};

export default ReturnsReceiving;
